// The day's ROLE and the values an exercise added to it starts with (#336,
// brief §8.2 and Appendix A.0, Antenore 12/10). No field anywhere: the role
// is read from the day's name, so it survives the save, holds for programs
// written by the connector, and changes when the day is renamed.
// Keep free of React and any UI import.

import type { AuthoredText } from "./authoredText.js";
import type { EffortScale } from "./effortScale.js";
import type { SetRowDraft } from "./programEdit.js";
import {
  defaultDeload,
  effortOn,
  harderSign,
  type DeloadEffect,
  type ProgressionRule,
  type TestEffect,
} from "./programProgression.js";
import { rowsForRule } from "./programProgressionApply.js";
import type { RoundType } from "./schemas/programV3.js";
import { normalizeSearch } from "./search.js";

/** The four seats of the tetrad, in its order. */
export type DayRole = "preparation" | "effort" | "deload" | "moderate";

/** The words that name each role, in the 5 languages. The source is the
 *  Tetras' own day names (the Metria templates in DDB: «1 · Preparazione»,
 *  «2 · Effort», «3 · Разгрузка», «4 · Mäßig», …): the tetrad model (#351)
 *  writes those names, and this reads them back. Compared without case or
 *  accents, so «PREPARATION» and «preparation» both match «Préparation». */
const ROLE_WORDS: ReadonlyArray<readonly [DayRole, readonly string[]]> = [
  ["preparation", ["Preparation", "Preparazione", "Préparation", "Vorbereitung", "Подготовка"]],
  ["effort", ["Effort", "Sforzo", "Anstrengung", "Усилие"]],
  ["deload", ["Deload", "Scarico", "Décharge", "Entlastung", "Разгрузка"]],
  ["moderate", ["Moderate", "Moderato", "Modéré", "Mäßig", "Умеренный"]],
];

const NORMALIZED = ROLE_WORDS.map(
  ([role, words]) => [role, words.map(normalizeSearch)] as const,
);

/** Every language of a stored name, plus what the editor shows. */
const namesOf = (session: { name: string; nameRaw?: AuthoredText }): string[] => {
  const raw = session.nameRaw;
  const stored =
    raw === undefined
      ? []
      : typeof raw === "string"
        ? [raw]
        : Object.values(raw).filter((v): v is string => typeof v === "string");
  return [session.name, ...stored];
};

const LETTERS = "a-z\u00df-\u024f\u0400-\u04ff";
/** Numbering or a day code in front: «1 · », «D2 », «3) ». */
const LEAD = new RegExp(`^[^${LETTERS}]*(?:d\\d+[^${LETTERS}]*)?`);

/** The first word of a name once its numbering is gone, after normalizeSearch
 *  (lowercase, accents gone): «1 · Preparazione» and «D2 Sforzo pesante» give
 *  «preparazione» and «sforzo». */
const leadWord = (name: string): string =>
  normalizeSearch(name).replace(LEAD, "").split(new RegExp(`[^${LETTERS}]`))[0] ?? "";

/** The day's role, from its name in any of its languages: the role word must
 *  OPEN the name, as a whole word, after any numbering or day code. So
 *  «2 · Effort» and «Sforzo» have a role, while «Low effort», «Effortless» and
 *  «Gleichmäßig» do not. A day named «Push», or with no name, has none: null. */
export const dayRoleOf = (session: { name: string; nameRaw?: AuthoredText }): DayRole | null => {
  const leads = new Set(namesOf(session).map(leadWord));
  for (const [role, roleWords] of NORMALIZED) {
    if (roleWords.some((w) => leads.has(w))) return role;
  }
  return null;
};

/** What the exercise is to the seed: a fundamental lift (it has an estimated
 *  1RM, `hasP1RM`), an accessory, or a timed exercise (`isTimeBased`). */
export type SeedKind = "fundamental" | "accessory" | "timed";

/** A fundamental lift: the catalog gives it an estimated 1RM (19 lifts).
 *  It errs only on the barbell row, which passes for fundamental. */
const isFundamental = (exercise: { hasP1RM?: boolean } | undefined): boolean =>
  exercise?.hasP1RM === true;

/** `isTimeBased` wins: a timed exercise has no reps to prescribe. Not in the
 *  catalog (not loaded yet, or a custom slug): an accessory. */
export const seedKindOf = (
  exercise: { hasP1RM?: boolean; isTimeBased?: boolean | null } | undefined,
): SeedKind =>
  exercise?.isTimeBased === true ? "timed" : isFundamental(exercise) ? "fundamental" : "accessory";

type Seed = { reps: number; rpe: readonly number[] };

/** Appendix A.0, the normal round. A range in the table takes the per-set
 *  values T1 W1 gives (Preparazione 4×5 R6/6/7/7, Scarico 3×5 R5/5/6) and the
 *  low end of a rep range (Moderato 3×10-12 is 3×10). `null`: the day with no
 *  role, «moderate-like» by Antenore's choice (3×8 / 3×10 at RPE 7). */
const SEEDS: Record<DayRole | "none", { fundamental: Seed; accessory: Seed }> = {
  preparation: { fundamental: { reps: 5, rpe: [6, 6, 7, 7] }, accessory: { reps: 8, rpe: [7, 7, 7] } },
  effort: { fundamental: { reps: 3, rpe: [8, 8, 8, 8] }, accessory: { reps: 5, rpe: [8, 8, 8] } },
  deload: { fundamental: { reps: 5, rpe: [5, 5, 6] }, accessory: { reps: 10, rpe: [6, 6, 6] } },
  moderate: { fundamental: { reps: 8, rpe: [7, 7, 7] }, accessory: { reps: 10, rpe: [7, 7, 7] } },
  none: { fundamental: { reps: 8, rpe: [7, 7, 7] }, accessory: { reps: 10, rpe: [7, 7, 7] } },
};

/** Appendix A.0, what the test and deload rounds do on a day of each role.
 *  Deltas in RPE (harder is up). «+1 RPE sulle serie di testa» is +1 on the
 *  round's hardest sets. `null`: the §5.3 default (defaultDeload). */
const EFFECTS: Record<DayRole | "none", { test: Record<"fundamental" | "accessory", TestEffect>; deload: DeloadEffect | null }> = {
  preparation: { test: { fundamental: { delta: 1, on: "top" }, accessory: { delta: 1, on: "top" } }, deload: { delta: -2 } },
  effort: {
    test: { fundamental: { amrapLast: true, delta: 1, on: "last" }, accessory: {} },
    deload: { delta: -2, setsDelta: -1 },
  },
  deload: { test: { fundamental: {}, accessory: {} }, deload: {} },
  moderate: { test: { fundamental: {}, accessory: {} }, deload: { delta: -1 } },
  none: { test: { fundamental: {}, accessory: {} }, deload: null },
};

/** A timed exercise: 3 sets of 30 s, at the accessory's effort of the day. */
const TIMED_SECONDS = 30;

/** The rule a new exercise starts with on a day of `role` (§8.2): the role's
 *  values, constant across the normal rounds, and its test and deload
 *  effects. The effort is written on the athlete's own scale: RPE as the
 *  table gives it, or RIR (10 − RPE). No load, so no MAX is needed. */
export const seedRuleForRole = (role: DayRole | null, kind: SeedKind, effortScale: EffortScale): ProgressionRule => {
  const table = SEEDS[role ?? "none"];
  const seed = kind === "fundamental" ? table.fundamental : table.accessory;
  const rpe = kind === "timed" ? seed.rpe.slice(0, 3) : seed.rpe;
  const values = rpe.map((v) => effortOn(v, effortScale));
  const base = values[0] ?? 0;
  const offsets = values.map((v) => v - base);
  const effects = EFFECTS[role ?? "none"];
  const test = effects.test[kind === "fundamental" ? "fundamental" : "accessory"];
  const deload = effects.deload ?? defaultDeload(effortScale);
  const onScale = (d: number | undefined) =>
    d === undefined || effects.deload === null ? {} : { delta: d * harderSign(effortScale) };
  return {
    axis: effortScale,
    ...(kind === "timed" ? { work: "countdown" as const } : {}),
    sets: rpe.length,
    reps: { kind: "constant", value: kind === "timed" ? TIMED_SECONDS : seed.reps },
    value: { kind: "constant", value: base },
    ...(offsets.some((o) => o !== 0) ? { perSet: { value: offsets } } : {}),
    test: { ...test, ...(test.delta === undefined ? {} : { delta: test.delta * harderSign(effortScale) }) },
    deload: { ...deload, ...onScale(deload.delta) },
  };
};

/** The set rows of seedRuleForRole over `cycleWeeks` rounds typed by
 *  `roundTypes` (absent: all normal, so every round gets the same row). */
export const seedSlotForRole = (
  role: DayRole | null,
  kind: SeedKind,
  cycleWeeks: number,
  effortScale: EffortScale,
  roundTypes: readonly RoundType[] = [],
): SetRowDraft[] => rowsForRule(seedRuleForRole(role, kind, effortScale), roundTypes, cycleWeeks);
